import { CloudUpload, WifiOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useOnline } from '@/lib/pwa';
import { useQueuedReports } from '@/features/project-sites/offline-queue';

/** Khóa hàng chờ chấm công của màn Chấm công (attendance-api.ts); ở đây chỉ đọc để đếm. */
const ATTENDANCE_QUEUE_KEY = 'mt_attendance_queue';
const countAttendanceQueue = () => {
  try {
    const v = JSON.parse(localStorage.getItem(ATTENDANCE_QUEUE_KEY) ?? '[]') as unknown;
    return Array.isArray(v) ? v.length : 0;
  } catch {
    return 0;
  }
};

/**
 * Thanh trạng thái mạng (X-PWA bước 2), nằm trên nội dung mọi trang:
 *  - Mất mạng / mở ứng dụng khi chưa có mạng: báo đang dùng bản lưu trên máy, thao tác sẽ chờ gửi.
 *  - Còn lượt chấm công chờ gửi: báo số lượt, bấm để mở màn Chấm công gửi lại.
 *  - Còn báo dự án lưu trên máy (CR-016 B3): báo số phiếu, bấm mở /du-an.
 */
export function OfflineBanner() {
  const online = useOnline();
  const { offline: offlineSession } = useAuth();
  const [pending, setPending] = useState(countAttendanceQueue);
  const sites = useQueuedReports().length;

  useEffect(() => {
    const refresh = () => setPending(countAttendanceQueue());
    window.addEventListener('storage', refresh);
    window.addEventListener('online', refresh);
    const t = setInterval(refresh, 5000);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('online', refresh);
      clearInterval(t);
    };
  }, []);

  if (online && !offlineSession && !pending && !sites) return null;
  if (!online || offlineSession)
    return (
      <div role="status" data-testid="offline-banner" className="flex items-center gap-2 border-b border-warning bg-warning-soft px-3 py-1.5 text-sm">
        <WifiOff className="size-4 shrink-0" />
        <span className="flex-1">
          <b>Mất mạng.</b> Đang dùng bản lưu trên máy; chấm công, báo dự án sẽ <b>chờ gửi</b> và tự gửi khi có mạng.
          {pending > 0 && ` Đang chờ gửi: ${pending} lượt chấm công.`}
          {sites > 0 && ` Đang chờ gửi: ${sites} báo dự án.`}
        </span>
      </div>
    );
  return (
    <Link
      to={pending ? '/cham-cong' : '/du-an'}
      role="status"
      data-testid="pending-banner"
      className="flex items-center gap-2 border-b border-info bg-info-soft px-3 py-1.5 text-sm text-info-foreground"
    >
      <CloudUpload className="size-4 shrink-0" />
      <span className="flex-1">
        <b>Chờ gửi:</b> {[pending && `${pending} lượt chấm công`, sites && `${sites} báo dự án`].filter(Boolean).join(', ')} lưu trên máy.{' '}
        {pending ? 'Bấm để mở Chấm công và gửi lại.' : 'Ứng dụng đang tự gửi; bấm để xem.'}
      </span>
    </Link>
  );
}
